import React, { useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import { useStudent } from "../../peoples/students/hooks/useStudent";
import { useCourse } from "../../configuration/course/hooks/useCourse";
import { useSession } from "../../configuration/session/hooks/useSession";
import { useFeeType } from "../../feetypes/hooks/useFeeType";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../core/data/slice/authSlice";

// Custom hooks
import { usePayShareFilters } from "../hooks/usePayShareFilters";
import { usePayShareData } from "../hooks/usePayShareData";
import { usePayShareTable } from "../hooks/usePayShareTable";
import { useStageManagement } from "../hooks/useStageManagement";
import { usePaySharePayment } from "../hooks/usePaySharePayment";
import { showToast } from "../../../core/utils/alert";
import { useCenter } from "../../peoples/centers/hooks/useCenter";

// Fallback fee type options in case API fails
const fallbackFeeTypes = [
  { id: 1, type: "coursefee", description: "Course Fee" },
  { id: 2, type: "registrationfee", description: "Registration Fee" },
];

const PayShareList = () => {
  const routes = all_routes;
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "superadmin";
  const navigate = useNavigate();

  // Use the custom filter hook
  const {
    selectedSession,
    selectedCourseId,
    selectedFeeType,
    selectedStage,
    selectedCenterId,
    recordsPerPage,
    currentPage,
    handleSessionChange,
    handleCourseChange,
    handleFeeTypeChange,
    handleStageChange,
    handlePaginationChange,
    areAllFiltersSelected,
    getMissingFiltersMessage,
    handleCenterChange,
  } = usePayShareFilters();

  // Get sessions from API
  const { getSessionState } = useSession({ size: 100 });
  const { sessions, isLoading: isSessionsLoading } = getSessionState();

  // Get courses from API
  const { getCourseState } = useCourse({ size: 100 });
  const { courses, isLoading: isCoursesLoading } = getCourseState();

  // Get fee types from API
  const { getFeeTypeState } = useFeeType({ size: 100 });
  const { feetypes, isLoading: isFeeTypesLoading } = getFeeTypeState();

  // Get centers from API only for superadmin
  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse, isLoading: isCentersLoading } = useCenterList();
  const centers = centerResponse?.data?.items || [];

  // Use the stage management hook
  const { availableStages, updateAvailableStages } =
    useStageManagement(courses);

  // Create a ref for the previous course ID to prevent unnecessary updates
  const prevCourseIdRef = useRef<number | null>(null);
  const updateInProgressRef = useRef<boolean>(false);

  // Update available stages when course changes - with safeguards against infinite loops
  useEffect(() => {
    // Skip if an update is already in progress
    if (updateInProgressRef.current) {
      return;
    }

    // Only update if course ID has changed or courses array has loaded for the first time
    const courseIdChanged = selectedCourseId !== prevCourseIdRef.current;
    const coursesLoaded =
      courses.length > 0 && prevCourseIdRef.current === null;

    if (courseIdChanged || coursesLoaded) {
      // Set flag to prevent concurrent updates
      updateInProgressRef.current = true;

      // Update ref
      prevCourseIdRef.current = selectedCourseId;

      // Schedule the update with a small delay to break potential cascading updates
      setTimeout(() => {
        updateAvailableStages(selectedCourseId, selectedStage, (newStage) => {
          if (newStage !== selectedStage) {
            handleStageChange({
              target: { value: newStage },
            } as React.ChangeEvent<HTMLSelectElement>);
          }
        });

        // Clear flag after update
        updateInProgressRef.current = false;
      }, 0);
    }
  }, [selectedCourseId, courses.length]); // Deliberately minimal dependencies

  // Get fee type code from selected fee type
  const feeTypeObj = feetypes.find(
    (ft) => ft.id.toString() === selectedFeeType
  );
  const feeTypeCode =
    feeTypeObj?.type ||
    (selectedFeeType === "1" ? "coursefee" : "registrationfee");

  // Use student hook with all filter params
  const { getStudentState, paginationData } = useStudent({
    size: recordsPerPage,
    page: currentPage,
    session_id: selectedSession ? Number(selectedSession) : undefined,
    course_id: selectedCourseId || undefined,
    stage_id: selectedStage ? Number(selectedStage) : undefined,
    fee_type: feeTypeCode || undefined,
    center_id: isSuperAdmin ? selectedCenterId || undefined : undefined,
    status: "active",
  });

  const { students, isLoading } = getStudentState();

  // Use the table hook
  const {
    selectedStudentKeys,
    setSelectedStudentKeys,
    handleSelectionChange,
    getCheckboxProps,
    handleSearch,
  } = usePayShareTable();

  // Use the data transformation hook
  const { payShareList } = usePayShareData(
    students,
    availableStages,
    feetypes,
    {
      selectedFeeType,
      selectedStage,
      selectedSession,
      selectedCourseId,
    }
  );

  // console.log(payShareList, 'psl--')

  // Use payment hook
  const { initiatePayment, isProcessing, calculateTotalAmount } =
    usePaySharePayment(setSelectedStudentKeys);

  // Get selected Claims
  const getSelectedPayShares = () => {
    return payShareList.filter((item) => selectedStudentKeys.includes(item.id));
  };

  // Handle bulk payment for selected students
  const handleBulkPayment = async () => {
    const selectedShares = getSelectedPayShares();

    if (selectedShares.length === 0) {
      showToast({
        title: "Error",
        text: "No students selected for payment",
        icon: "error",
      });
      return;
    }

    // Get fee type code from selected fee type
    const feeTypeObj = feetypes.find(
      (ft) => ft.id.toString() === selectedFeeType
    );
    const feeTypeCode =
      feeTypeObj?.type ||
      (selectedFeeType === "1" ? "coursefee" : "registrationfee");

    // Add session_id and course_id to each share item
    const enrichedShares = selectedShares.map((share) => ({
      ...share,
      session_id: Number(selectedSession),
      course_id: selectedCourseId || 0,
    }));

    try {
      // console.log(enrichedShares, 'en_share')
      await initiatePayment(enrichedShares, feeTypeCode);
    } catch (error) {
      console.error("Payment failed:", error);
      showToast({
        title: "Error",
        text: "Payment failed. Please try again.",
        icon: "error",
      });
    }
  };

  // Update loading state for filter dropdowns - only include isCentersLoading for superadmin
  const isFiltersLoading =
    isSessionsLoading ||
    isCoursesLoading ||
    isFeeTypesLoading ||
    (isSuperAdmin && isCentersLoading); // Only include centers loading for superadmin

  // Calculate total amount for selected shares
  const totalSelectedAmount = calculateTotalAmount(getSelectedPayShares());

  // Table columns configuration
  const columns = [
    {
      title: "Student ID",
      dataIndex: "student_id",
      key: "student_id",
      render: (text: string, record: any) => (
        <a
          className="text-primary cursor-pointer"
          onClick={() => {
            navigate(
              `${routes.viewPayShare.replace(":id", record.id.toString())}`,
              { state: { payShare: record } }
            );
          }}
        >
          {text}
        </a>
      ),
      sorter: (a: any, b: any) => a.student_id.localeCompare(b.student_id),
    },
    {
      title: "Student Name",
      dataIndex: "student_name",
      key: "student_name",
      sorter: (a: any, b: any) => a.student_name.localeCompare(b.student_name),
    },
    // Conditionally include center column for superadmin
    ...(isSuperAdmin
      ? [
        {
          title: "Center",
          dataIndex: "center_name",
          key: "center_name",
          sorter: (a: any, b: any) =>
            a.center_name.localeCompare(b.center_name),
        },
      ]
      : []),
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) =>
        amount > 0 ? (
          `₹${amount.toLocaleString()}`
        ) : (
          <span className="text-muted fst-italic fs-12">No fee available</span>
        ),
      sorter: (a: any, b: any) => a.amount - b.amount,
    },
    {
      title: "Share Amount",
      dataIndex: "share_amount",
      key: "share_amount",
      render: (amount: number) =>
        amount > 0 ? (
          `₹${amount.toLocaleString()}`
        ) : (
          <span className="text-muted fst-italic fs-12">No fee available</span>
        ),
      sorter: (a: any, b: any) => a.share_amount - b.share_amount,
    },
    {
      title: "Status",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (status: string) => (
        <span
          className={`badge badge-soft-${status === "paid" ? "success" : "warning"
            } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
      sorter: (a: any, b: any) =>
        a.payment_status.localeCompare(b.payment_status),
    },
    {
      title: "Action",
      key: "actions",
      render: (record: any) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to={`${routes.viewPayShare.replace(
                    ":id",
                    record.id.toString()
                  )}`}
                  state={{ payShare: record }}
                >
                  <i className="ti ti-eye me-2" />
                  View Details
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Claims</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.centerDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Claims</li>
                <li className="breadcrumb-item active" aria-current="page">
                  All Claims
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap">
            <h4>Claims</h4>
            {/* Only show payment button if not superadmin */}
            {!isSuperAdmin && selectedStudentKeys.length > 0 && (
              <div className="d-flex align-items-center">
                <span className="me-3">
                  {selectedStudentKeys.length} student
                  {selectedStudentKeys.length !== 1 ? "s" : ""} selected
                </span>
                <span className="me-3 fw-bold">
                  Total: ₹{totalSelectedAmount.toLocaleString()}
                </span>
                <button
                  className="btn btn-primary"
                  onClick={handleBulkPayment}
                  disabled={isProcessing || totalSelectedAmount <= 0}
                >
                  {isProcessing ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Processing...
                    </>
                  ) : (
                    "Pay Claim For Selected"
                  )}
                </button>
              </div>
            )}
          </div>
          <div className="card-body">
            {/* Filters Section */}
            <div className="row mb-4">
              <>
                {/* Add center dropdown for superadmin */}
                {isSuperAdmin ? (
                  // For superadmin: equal width columns on large screens, responsive on small screens
                  <>
                    <div className="col-lg col-md-4 col-sm-6 mb-3">
                      <label className="form-label">Select Center</label>
                      <select
                        className="form-select"
                        value={selectedCenterId || ""}
                        onChange={handleCenterChange}
                        required
                      >
                        <option value="">Select Center</option>
                        {centers.map((center) => (
                          <option key={center.id} value={center.id.toString()}>
                            {center.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-lg col-md-4 col-sm-6 mb-3">
                      <label className="form-label">Select Session</label>
                      <select
                        className="form-select"
                        value={selectedSession}
                        onChange={handleSessionChange}
                        required
                      >
                        <option value="">Select Session</option>
                        {sessions.map((session) => (
                          <option
                            key={session.id}
                            value={session.id.toString()}
                          >
                            {session.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-lg col-md-4 col-sm-6 mb-3">
                      <label className="form-label">Fee Type</label>
                      <select
                        className="form-select"
                        value={selectedFeeType}
                        onChange={(e) => handleFeeTypeChange(e, feetypes)}
                        required
                      >
                        <option value="">Select Fee Type</option>
                        {feetypes.length > 0
                          ? feetypes.map((feetype) => (
                            <option
                              key={feetype.id}
                              value={feetype.id.toString()}
                            >
                              {feetype.type === "coursefee"
                                ? "Course Fee"
                                : "Registration Fee"}
                            </option>
                          ))
                          : fallbackFeeTypes.map((feetype) => (
                            <option
                              key={feetype.id}
                              value={feetype.id.toString()}
                            >
                              {feetype.type === "coursefee"
                                ? "Course Fee"
                                : "Registration Fee"}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-lg col-md-4 col-sm-6 mb-3">
                      <label className="form-label">Select Course</label>
                      <select
                        className="form-select"
                        value={selectedCourseId || ""}
                        onChange={handleCourseChange}
                        required
                        disabled={!selectedSession}
                      >
                        <option value="">Select Course</option>
                        {courses.map((course) => (
                          <option key={course.id} value={course.id.toString()}>
                            {course.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Stage dropdown for superadmin */}
                    {(selectedFeeType === "1" ||
                      feetypes.find(
                        (ft) => ft.id.toString() === selectedFeeType
                      )?.type === "coursefee") && (
                        <div className="col-lg col-md-4 col-sm-6 mb-3">
                          <label className="form-label">Level</label>
                          <select
                            className="form-select"
                            value={selectedStage}
                            onChange={handleStageChange}
                            required
                            disabled={!selectedCourseId}
                          >
                            <option value="">Select Level</option>
                            {availableStages.map((stage) => (
                              <option key={stage.id} value={stage.id.toString()}>
                                {stage.name}
                              </option>
                            ))}
                          </select>
                          {/* {selectedCourseId && availableStages.length === 0 && (
                          <div className="text-muted small mt-1">
                            No stages available for this course
                          </div>
                        )} */}
                        </div>
                      )}
                  </>
                ) : (
                  // For center admin: keep existing layout
                  <>
                    <div
                      className={
                        selectedFeeType === "1" ||
                          feetypes.find(
                            (ft) => ft.id.toString() === selectedFeeType
                          )?.type === "coursefee"
                          ? "col-lg-3 col-md-4 col-sm-6 mb-3"
                          : "col-lg-4 col-md-6 col-sm-12 mb-3"
                      }
                    >
                      <label className="form-label">Select Session</label>
                      <select
                        className="form-select"
                        value={selectedSession}
                        onChange={handleSessionChange}
                        required
                      >
                        <option value="">Select Session</option>
                        {sessions.map((session) => (
                          <option
                            key={session.id}
                            value={session.id.toString()}
                          >
                            {session.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div
                      className={
                        selectedFeeType === "1" ||
                          feetypes.find(
                            (ft) => ft.id.toString() === selectedFeeType
                          )?.type === "coursefee"
                          ? "col-lg-3 col-md-4 col-sm-6 mb-3"
                          : "col-lg-4 col-md-6 col-sm-12 mb-3"
                      }
                    >
                      <label className="form-label">Fee Type</label>
                      <select
                        className="form-select"
                        value={selectedFeeType}
                        onChange={(e) => handleFeeTypeChange(e, feetypes)}
                        required
                      >
                        <option value="">Select Fee Type</option>
                        {feetypes.length > 0
                          ? feetypes.map((feetype) => (
                            <option
                              key={feetype.id}
                              value={feetype.id.toString()}
                            >
                              {feetype.type === "coursefee"
                                ? "Course Fee"
                                : "Registration Fee"}
                            </option>
                          ))
                          : fallbackFeeTypes.map((feetype) => (
                            <option
                              key={feetype.id}
                              value={feetype.id.toString()}
                            >
                              {feetype.type === "coursefee"
                                ? "Course Fee"
                                : "Registration Fee"}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div
                      className={
                        selectedFeeType === "1" ||
                          feetypes.find(
                            (ft) => ft.id.toString() === selectedFeeType
                          )?.type === "coursefee"
                          ? "col-lg-3 col-md-4 col-sm-6 mb-3"
                          : "col-lg-4 col-md-6 col-sm-12 mb-3"
                      }
                    >
                      <label className="form-label">Select Course</label>
                      <select
                        className="form-select"
                        value={selectedCourseId || ""}
                        onChange={handleCourseChange}
                        required
                        disabled={!selectedSession}
                      >
                        <option value="">Select Course</option>
                        {courses.map((course) => (
                          <option key={course.id} value={course.id.toString()}>
                            {course.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Stage dropdown for center admin */}
                    {(selectedFeeType === "1" ||
                      feetypes.find(
                        (ft) => ft.id.toString() === selectedFeeType
                      )?.type === "coursefee") && (
                        <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
                          <label className="form-label">Stage</label>
                          <select
                            className="form-select"
                            value={selectedStage}
                            onChange={handleStageChange}
                            required
                            disabled={!selectedCourseId}
                          >
                            <option value="">Select Level</option>
                            {availableStages.map((stage) => (
                              <option key={stage.id} value={stage.id.toString()}>
                                {stage.name}
                              </option>
                            ))}
                          </select>
                          {/* {selectedCourseId && availableStages.length === 0 && (
                            <div className="text-muted small mt-1">
                              No stages available for this course
                            </div>
                          )} */}
                        </div>
                      )}
                  </>
                )}
              </>
            </div>

            {/* Table Section */}
            {areAllFiltersSelected(feetypes, isSuperAdmin) ? (
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={payShareList}
                  Selection={!isSuperAdmin}
                  onSelectionChange={handleSelectionChange}
                  rowKey="id"
                  selectedKeys={selectedStudentKeys}
                  getCheckboxProps={getCheckboxProps}
                  pagination={{
                    current: currentPage,
                    pageSize: recordsPerPage,
                    total: paginationData?.totalItems || 0,
                    onChange: handlePaginationChange,
                    pageSizeOptions: ["All", 10, 20, 30],
                  }}
                  onSearchChange={(value) =>
                    handleSearch(value, () => handlePaginationChange(1))
                  }
                />
              </div>
            ) : (
              <div className="text-center py-5">
                <i className="ti ti-filter fs-1 text-muted mb-3"></i>
                <h4>Please select all filters to view the list</h4>
                <p className="text-warning">
                  {getMissingFiltersMessage(feetypes, isSuperAdmin)
                    ? `Missing filters: ${getMissingFiltersMessage(
                      feetypes,
                      isSuperAdmin
                    )}`
                    : "Select all filters to see the claim list"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayShareList;
